import { useEffect, useState } from "react"
import { Package, ShoppingCart, X } from "lucide-react"
import { useGetBookInventoryDemandClassesQuery } from "shared/redux/endpoints/procurementItems"
import { Button, Input, NumberInput, Textarea } from "../../ui"
import BookThumb from "./BookThumb"
import { ClassTable, classesWithin } from "./DemandDetailDrawer"
import { at, formatQty, num, shortageQuantity } from "../../bookInventory/model"

const defaultReason = (days) => `Replenish stock for upcoming classes (${days} days).`

// A short confirm step before the full Order Request form: the user checks the
// quantity and reason here, the form (category, region, group item, approval)
// does the rest. Nothing is created from this modal.
const CreateOrderModal = ({ open, row, daysWindow, onClose, onContinue }) => {
  // Same cache entry as the detail drawer, so opening from there is instant.
  const { data, isFetching } = useGetBookInventoryDemandClassesQuery(
    { centerId: row?.centerId, itemId: row?.itemId, daysWindow },
    { skip: !open || row?.centerId == null || row?.itemId == null }
  )
  const item = data?.item
  const classes = classesWithin(data?.classes || [], daysWindow)
  const suggested = row ? shortageQuantity(row, daysWindow) : 0
  const [quantity, setQuantity] = useState("")
  const [reason, setReason] = useState(defaultReason(daysWindow))

  useEffect(() => {
    if (!open) return
    setQuantity(String(suggested > 0 ? suggested : 1))
    setReason(defaultReason(daysWindow))
  }, [open, row?.centerId, row?.itemId]) // eslint-disable-line

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  if (!open || !row) return null

  const qty = Number(quantity)
  const valid = Number.isFinite(qty) && qty > 0
  const stock = item ? item.quantityOnHand : row.currentStock ?? row.stockRemaining
  const shortage = at(row, "actualShortage", daysWindow)

  return (
    <div className="ds-drawer-root">
      <div className="ds-drawer-backdrop bi-modal-backdrop" onClick={onClose} aria-hidden />
      <div className="bi-modal" role="dialog" aria-modal="true" aria-label="Create Order Request">
        <div className="bi-modal-head">
          <span className="bi-modal-icon" aria-hidden>
            <ShoppingCart />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="pc-h1" style={{ fontSize: 18 }}>Create Order Request</h2>
            <div className="bi-muted" style={{ margin: 0 }}>
              Prefilled from current demand. You finish and submit the order on the next screen.
            </div>
          </div>
          <button type="button" className="pc-btn pc-btn--ghost pc-btn--sm" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden />
          </button>
        </div>

        <div className="bi-modal-body">
          <div className="bi-modal-grid">
            <Input label="Center" value={row.centerName || `Center ${row.centerId}`} disabled readOnly />
            <div className="pc-field">
              <span className="pc-label">Item</span>
              <div className="bi-item-box">
                <BookThumb key={item?.imageUrl || row.imageUrl || "none"} src={item?.imageUrl || row.imageUrl} />
                <span style={{ minWidth: 0 }}>
                  <span className="bi-book-name">{row.itemName || item?.itemName || "—"}</span>
                  {(row.itemCode || item?.itemCode) && <div className="bi-book-code">{row.itemCode || item?.itemCode}</div>}
                </span>
              </div>
            </div>
            <NumberInput
              label="Quantity"
              required
              value={quantity}
              onChange={setQuantity}
              hint={
                suggested > 0
                  ? `Suggested qty: ${formatQty(suggested)} (based on the shortage)`
                  : shortage == null
                    ? "Shortage not calculated yet — enter the quantity to order."
                    : "Stock covers current demand — adjust if you still want to order."
              }
            />
          </div>
          <div style={{ marginTop: 14 }}>
            <Textarea label="Reason / Notes" rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
          </div>

          <div className="bi-modal-split">
            <div style={{ minWidth: 0 }}>
              <div className="bi-section-title">Related classes ({classes.length})</div>
              {isFetching ? (
                <p className="bi-muted">Loading classes…</p>
              ) : classes.length ? (
                <ClassTable classes={classes} compact />
              ) : (
                <p className="bi-muted">No class needing this book starts in the next {daysWindow} days.</p>
              )}
            </div>
            <div className="bi-stock-box">
              <div className="bi-section-title">
                <Package size={16} aria-hidden /> Stock information (current)
              </div>
              <dl>
                <dt>Current stock</dt>
                <dd>{formatQty(stock)} books</dd>
                <dt>Actual demand +30% (next {daysWindow} days)</dt>
                <dd>{formatQty(at(row, "actualNeeded", daysWindow))} books</dd>
                <dt>Actual shortage ({daysWindow} days)</dt>
                <dd className={shortage == null ? undefined : `bi-gap bi-gap--${num(shortage) > 0 ? "neg" : "zero"}`}>
                  {formatQty(shortage)} books
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bi-modal-foot">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            leftIcon={<ShoppingCart size={15} aria-hidden />}
            disabled={!valid}
            onClick={() => onContinue(row, { quantity: Math.ceil(qty), reason: reason.trim() })}
          >
            Continue to order form
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CreateOrderModal
